/**
 * Infer what kind of tub a scanned `bathtub` box is, from the walls around it.
 *
 * RoomPlan only reports a box; the install type has to be read off the room:
 *   - walls hugging three sides → alcove (apron-front tub in a recess)
 *   - otherwise a large square-ish footprint → drop-in (deck-mounted soaker)
 *   - otherwise → undermount (the generated-surround assembly)
 *
 * Pure geometry; no I/O.
 */
const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { calculateWallCorners } = require('../geometry/wallGeometry');
const { getObjectCorners } = require('../collision/geometry');

const IN = 0.0254;

/** How far a wall may sit off a tub face and still count as hugging it. */
const WALL_HUG_TOLERANCE_M = 4 * IN;
/** Both footprint sides at/over this → a drop-in soaker, not a standard tub. */
const LARGE_TUB_THRESHOLD_M = 50 * IN;
/**
 * Generated-footprint thresholds for `resolveTubType` (planned tubs only —
 * the scan heuristic never uses these). A wall-seated footprint at deck size
 * is a planned undermount platform: 64" is the smallest surround that admits
 * an active catalog undermount tub with its 4" deck lip (smallest model is
 * 59.1" wide), and 32" is where a surround starts reading as a real deck
 * (deck tubs run 32-36" deep). Larger still — an 80" × 48" footprint — is an
 * open soaker space: emit a freestanding tub, no surround at all.
 */
const DECK_MIN_WIDTH_M = 64 * IN;
const DECK_MIN_DEPTH_M = 32 * IN;
const FREESTANDING_MIN_WIDTH_M = 80 * IN;
const FREESTANDING_MIN_DEPTH_M = 48 * IN;
/** Hugged sides needed to call a tub an alcove (back + both ends). */
const ALCOVE_MIN_HUGGED_SIDES = 3;
/**
 * A wall must run along at least this fraction of a face to hug it — a short
 * partition stub an inch off the apron is not a recess wall.
 */
const MIN_FACE_OVERLAP_FRACTION = 0.5;

/** Rotate a local XZ offset by the object's Y rotation (three.js convention). */
function localToWorldXZ(center, rotationY, lx, lz) {
  const c = Math.cos(rotationY);
  const s = Math.sin(rotationY);
  return { x: center.x + lx * c + lz * s, z: center.z - lx * s + lz * c };
}

/**
 * The four side faces of a box in XZ: midpoint, outward normal, half-length
 * along the face.
 */
function boxSidesXZ(obj) {
  const ry = obj.rotation?.y || 0;
  const hw = obj.dimensions.x / 2;
  const hd = obj.dimensions.z / 2;
  const c = obj.center;
  const sides = [
    { mid: localToWorldXZ(c, ry, 0, -hd), halfLen: hw, lx: 0, lz: -1 },
    { mid: localToWorldXZ(c, ry, 0, hd), halfLen: hw, lx: 0, lz: 1 },
    { mid: localToWorldXZ(c, ry, -hw, 0), halfLen: hd, lx: -1, lz: 0 },
    { mid: localToWorldXZ(c, ry, hw, 0), halfLen: hd, lx: 1, lz: 0 },
  ];
  return sides.map((s) => {
    const n = localToWorldXZ({ x: 0, z: 0 }, ry, s.lx, s.lz);
    return { mid: s.mid, normal: n, halfLen: s.halfLen };
  });
}

/**
 * Does `wall` hug this box side? Parallel-ish, within `tolerance` of the face
 * plane, and running along at least MIN_FACE_OVERLAP_FRACTION of the face.
 */
function wallHugsSide(side, axes, tolerance) {
  // Perpendicular distance from the face midpoint to the wall line.
  const dx = side.mid.x - axes.start.x;
  const dz = side.mid.z - axes.start.z;
  const perp = Math.abs(dx * axes.wallNormal.x + dz * axes.wallNormal.z);
  if (perp > tolerance) return false;
  // The wall must run along the face (not across it).
  const alongness = Math.abs(
    side.normal.x * axes.wallNormal.x + side.normal.z * axes.wallNormal.z
  );
  if (alongness < Math.SQRT1_2) return false;
  // Overlap of the face's span [mid - halfLen, mid + halfLen] with the wall
  // segment [0, wallLen], measured along the wall.
  const midParam = dx * axes.wallDir.x + dz * axes.wallDir.z;
  const faceMin = midParam - side.halfLen;
  const faceMax = midParam + side.halfLen;
  const overlap = Math.min(faceMax, axes.wallLen) - Math.max(faceMin, 0);
  return overlap >= MIN_FACE_OVERLAP_FRACTION * 2 * side.halfLen;
}

/**
 * Wall axes from `alignment.bottomLeft/bottomRight` when the pipeline has
 * written them, else derived from center + rotation + length (RoomPlan room
 * walls that never went through alignment — ~a third of rooms in dev).
 */
function wallAxesWithFallback(wall) {
  const aligned = getWallAxes(wall);
  if (aligned) return aligned;
  if (!wall?.center || !wall?.dimensions) return null;
  const corners = calculateWallCorners(wall);
  if (!corners) return null;
  return getWallAxes({
    alignment: {
      bottomLeft: corners.bottomLeft,
      bottomRight: corners.bottomRight,
    },
  });
}

/**
 * Which of the box's four sides have a wall running along them, in
 * `boxSidesXZ` order: local -Z, +Z (depth faces), -X, +X (width ends).
 * @param {{center, dimensions, rotation}} obj
 * @param {Array} walls room walls (need `alignment.bottomLeft/bottomRight`)
 * @param {number} [tolerance] metres
 * @returns {boolean[]} length 4
 */
function wallHuggedSideFlags(obj, walls, tolerance = WALL_HUG_TOLERANCE_M) {
  if (!obj?.center || !obj?.dimensions) return [false, false, false, false];
  const axesList = (walls || []).map(wallAxesWithFallback).filter(Boolean);
  return boxSidesXZ(obj).map((side) =>
    axesList.some((axes) => wallHugsSide(side, axes, tolerance))
  );
}

/**
 * Count how many of the box's four sides have a wall running along them.
 */
function countWallHuggedSides(obj, walls, tolerance = WALL_HUG_TOLERANCE_M) {
  return wallHuggedSideFlags(obj, walls, tolerance).filter(Boolean).length;
}

/**
 * @returns {'alcove'|'drop-in'|'undermount'}
 */
/**
 * Distance from `obj`'s center to the nearest wall line in direction `n`
 * (unit, world XZ), considering only walls that hug the side facing `n`.
 * null when no wall hugs that side.
 */
function distanceToHuggingWall(obj, side, axesList, tolerance) {
  let best = null;
  for (const axes of axesList) {
    if (!wallHugsSide(side, axes, tolerance)) continue;
    const dx = axes.start.x - obj.center.x;
    const dz = axes.start.z - obj.center.z;
    // Perpendicular distance from the center to the wall line, measured
    // along the side's outward normal. A wall hugging this side lies on
    // the positive side of the normal.
    const along = Math.abs(dx * axes.wallNormal.x + dz * axes.wallNormal.z);
    const cosine = Math.abs(
      side.normal.x * axes.wallNormal.x + side.normal.z * axes.wallNormal.z
    );
    const d = cosine > 1e-9 ? along / cosine : along;
    if (best === null || d < best) best = d;
  }
  return best;
}

/**
 * Wall-to-wall spans available to `obj` along its own local axes: `width`
 * (local x) and `depth` (local z). A span is only known when BOTH opposite
 * sides hug a wall (an alcove end-to-end, or a back wall with a facing
 * wall); otherwise that axis is null — "no bound". Used to cap the catalog
 * pick so a 60" model is never dropped into a 58.6" alcove just because
 * the scan box was 58" and the fit tolerance 3".
 *
 * @returns {{ width: number|null, depth: number|null }}
 */
function wallToWallSpans(obj, walls, tolerance = WALL_HUG_TOLERANCE_M) {
  if (!obj?.center || !obj?.dimensions) return { width: null, depth: null };
  const axesList = (walls || []).map(wallAxesWithFallback).filter(Boolean);
  // boxSidesXZ order: -z, +z (depth faces), -x, +x (width faces)
  const [minZ, maxZ, minX, maxX] = boxSidesXZ(obj).map((side) =>
    distanceToHuggingWall(obj, side, axesList, tolerance)
  );
  const span = (a, b) => (a === null || b === null ? null : a + b);
  return { width: span(minX, maxX), depth: span(minZ, maxZ) };
}

/**
 * Distance from `center` along `axis * sign` to the nearest wall that CROSSES
 * that ray — the one notion of "a wall that bounds this box in this direction".
 *
 * Two tests, and the second is the one that is easy to leave out. The wall must
 * run ACROSS the ray rather than along it, AND it must actually span the object
 * where the ray meets it (`halfSpan` either side of the foot). Without the
 * second, any wall in the room whose projection happens to land near the object
 * reads as a bound: `verifyTub` filtered on orientation alone and had a wall 88"
 * away, on the far side of the bathroom, clipping a tub surround it never
 * touched.
 *
 * @param {{x: number, z: number}} center - the object's centre, world XZ
 * @param {{x: number, z: number}} axis - unit direction to cast along
 * @param {number} halfSpan - half the object's extent across the ray
 * @param {Array} axesList - `wallAxesWithFallback` results
 * @param {number} sign - +1 or -1, which way along `axis` to cast
 * @returns {number|null} distance to the nearest bound, null when unbounded
 */
function crossingWallDistance(center, axis, halfSpan, axesList, sign) {
  let best = null;
  for (const axes of axesList) {
    const alongness = Math.abs(
      axis.x * axes.wallNormal.x + axis.z * axes.wallNormal.z
    );
    if (alongness < Math.SQRT1_2) continue;
    const dx = axes.start.x - center.x;
    const dz = axes.start.z - center.z;
    // Signed distance from the centre to the wall line, along the ray.
    const perp = dx * axes.wallNormal.x + dz * axes.wallNormal.z;
    const denom = axis.x * axes.wallNormal.x + axis.z * axes.wallNormal.z;
    const d = (perp / denom) * sign;
    if (d <= 0) continue;
    const foot = {
      x: center.x + axis.x * d * sign,
      z: center.z + axis.z * d * sign,
    };
    const t =
      (foot.x - axes.start.x) * axes.wallDir.x +
      (foot.z - axes.start.z) * axes.wallDir.z;
    if (t < -halfSpan || t > axes.wallLen + halfSpan) continue;
    if (best === null || d < best) best = d;
  }
  return best;
}

/**
 * Distance from `center` along `axis * sign` to the nearest solid FIXTURE
 * that stands in the way — the same question `crossingWallDistance` asks of
 * walls, and it has to be asked of both. A tub's run along its wall is ended
 * by whatever is actually there: the reported room had a shower taking 39" of
 * a 97.8" wall, and a walls-only measurement called that wall entirely the
 * tub's, planned a 63.9" deck into 58.9" of free run, and lost the assembly.
 *
 * Same two tests as the wall version, read off the footprint instead of a
 * line: the obstacle must lie AHEAD along the ray, and it must overlap the
 * band the tub sweeps (`halfSpan` either side of the axis) — otherwise the
 * vanity on the facing wall bounds a tub it stands nowhere near.
 *
 * @param {{x: number, z: number}} center - the tub's centre, world XZ
 * @param {{x: number, z: number}} axis - unit direction to cast along
 * @param {number} halfSpan - half the tub's extent across the ray
 * @param {Array} obstacles - room objects ({center, dimensions, rotation})
 * @param {number} sign - +1 or -1, which way along `axis` to cast
 * @returns {number|null} distance to the nearest bound, null when unbounded
 */
function crossingObstacleDistance(center, axis, halfSpan, obstacles, sign) {
  // The band's axis is the tub's own local +Z, i.e. `axis` turned a quarter
  // turn — kept as vectors rather than an angle so no handedness can creep in.
  const across = { x: -axis.z, z: axis.x };
  let best = null;
  for (const obj of obstacles || []) {
    if (!obj?.center || !obj?.dimensions) continue;
    let alongMin = Infinity;
    let acrossMin = Infinity;
    let acrossMax = -Infinity;
    for (const corner of getObjectCorners(obj)) {
      const dx = corner.x - center.x;
      const dz = corner.z - center.z;
      alongMin = Math.min(alongMin, (dx * axis.x + dz * axis.z) * sign);
      const a = dx * across.x + dz * across.z;
      acrossMin = Math.min(acrossMin, a);
      acrossMax = Math.max(acrossMax, a);
    }
    // Behind the centre, or already overlapping it — not a bound in this
    // direction. An intruder that far in is a collision, answered elsewhere.
    if (alongMin <= 0) continue;
    if (acrossMax < -halfSpan || acrossMin > halfSpan) continue;
    if (best === null || alongMin < best) best = alongMin;
  }
  return best;
}

/**
 * How far the tub can reach along each end of its own width axis before a
 * wall or a solid fixture stops it. `null` on a side means nothing does.
 *
 * The single measurement behind both "how wide is this alcove" and "does the
 * planned deck fit" — they are the same question read with different
 * tolerance for an open end, and splitting them is how the two drifted.
 *
 * @param {{center, dimensions, rotation}} obj
 * @param {Array} walls
 * @param {Array} [obstacles] solid neighbours (see `deriveBookendNeighbors`)
 * @returns {{axis: {x, z}, plus: number|null, minus: number|null}|null}
 */
function widthAxisReach(obj, walls, obstacles = []) {
  if (!obj?.center || !obj?.dimensions) return null;
  const axesList = (walls || []).map(wallAxesWithFallback).filter(Boolean);
  const ry = obj.rotation?.y || 0;
  // Local +x in world XZ (three.js Y-rotation convention).
  const axis = { x: Math.cos(ry), z: -Math.sin(ry) };
  const halfDepth = obj.dimensions.z / 2;

  const reach = (sign) => {
    const wall = crossingWallDistance(
      obj.center,
      axis,
      halfDepth,
      axesList,
      sign
    );
    const fixture = crossingObstacleDistance(
      obj.center,
      axis,
      halfDepth,
      obstacles,
      sign
    );
    if (wall === null) return fixture;
    if (fixture === null) return wall;
    return Math.min(wall, fixture);
  };

  return { axis, plus: reach(1), minus: reach(-1) };
}

/**
 * The free run along the tub's width axis: how much wall the tub and anything
 * built around it may occupy. An open end is unbounded, so this is Infinity
 * rather than null — the caller is asking "is there room", and there is.
 *
 * @param {{center, dimensions, rotation}} obj
 * @param {Array} walls
 * @param {Array} [obstacles]
 * @returns {number} metres, possibly Infinity
 */
function availableWidthRun(obj, walls, obstacles = []) {
  const reach = widthAxisReach(obj, walls, obstacles);
  if (!reach) return Infinity;
  return (reach.plus ?? Infinity) + (reach.minus ?? Infinity);
}

/**
 * The RECESS an alcove tub sits in, measured wall-to-wall along the tub's
 * own width axis — independent of where in that recess the tub currently
 * stands.
 *
 * `wallToWallSpans` cannot answer this once the tub has been seated flush:
 * it requires BOTH end faces to HUG a wall (4" tolerance), and a tub pushed
 * to one end leaves the far face the whole filler width away. That made the
 * measurement single-use — the first pass found a 53.5" recess, seated the
 * tub with a 7" filler, and the next reprocess measured nothing and fell
 * back to model + 4" deck margin, shrinking the filler to 3.9".
 *
 * So this casts from the tub's own CENTRE along ±width to the nearest wall
 * that faces it, rather than asking each face for a wall it is touching.
 * Both directions must find one — an open end is not a recess.
 *
 * A recess ends at whatever is solid, so `obstacles` bound it exactly as
 * walls do. Pass none and the answer is walls-only, as it always was.
 *
 * Returns the CENTRE offset as well as the width. The two are inseparable:
 * a scanned tub is rarely centred in its recess, so a footprint given the
 * recess width but the tub's own centre hangs off one end — 3.5" clear of
 * the far wall with the filler poking through the near one. `centerShift`
 * moves the footprint onto the recess along the width axis only; the depth
 * axis keeps the tub against its back wall.
 *
 * @param {{center, dimensions, rotation}} obj
 * @param {Array} walls
 * @param {Array} [obstacles] solid neighbours (see `deriveBookendNeighbors`)
 * @returns {{width: number, centerShift: {x: number, z: number}}|null} null
 *   when either direction is unbounded
 */
function alcoveRecess(obj, walls, obstacles = []) {
  const reach = widthAxisReach(obj, walls, obstacles);
  if (!reach) return null;
  const { axis, plus, minus } = reach;
  if (plus === null || minus === null) return null;
  // The recess centre sits half the difference toward the roomier side.
  const shift = (plus - minus) / 2;
  return {
    width: plus + minus,
    centerShift: { x: axis.x * shift, z: axis.z * shift },
  };
}

function inferScannedTubType(tub, walls) {
  if (countWallHuggedSides(tub, walls) >= ALCOVE_MIN_HUGGED_SIDES) {
    return 'alcove';
  }
  const w = tub.dimensions?.x || 0;
  const d = tub.dimensions?.z || 0;
  if (w >= LARGE_TUB_THRESHOLD_M && d >= LARGE_TUB_THRESHOLD_M) {
    return 'drop-in';
  }
  return 'undermount';
}

/**
 * The install type for a tub fixture WE planned, in the order the answer
 * should be trusted:
 *   1. an explicit `tubType` — the generator (or a prior apply) already
 *      decided, and its blueprint is authoritative;
 *   2. a tub-shower combo is always an alcove (`types.js` only allows the
 *      combo in a qualifying 60" recess);
 *   3. three hugged sides → alcove — a recess bounds the install whatever
 *      its size;
 *   4. otherwise, seated against a wall, the planned footprint says what
 *      was planned: ≥80" × ≥48" → freestanding (an open soaker space),
 *      ≥50" × ≥50" → drop-in (a square soaker), ≥64" × ≥32" → undermount
 *      (a deck-sized platform), anything smaller → alcove; touching
 *      nothing → freestanding.
 *
 * Step 4 is where this parts ways with `inferScannedTubType`, and the
 * difference is the point. That function classifies a box RoomPlan handed
 * us: when it can't tell, wrapping the unknown box in a generated surround
 * (undermount) is the safe read. Here we are not guessing — we placed the
 * tub, chose its size, and seated it on a wall. An ordinary skirted tub
 * against one or two walls is an alcove tub; calling it undermount demands
 * a deck-sized surround the generator never planned, and the catalog query
 * then matches nothing and drops the tub. In the real world a deck is built
 * on purpose — so undermount is only ever emitted when the footprint IS
 * deck-sized, meaning the generator planned the platform, and never as a
 * fall-through for an unclassifiable tub.
 *
 * Both sides of the wire call this (layout conversion and apply) so they
 * can never disagree about what kind of tub was planned.
 *
 * @param {{type, center, dimensions, rotation, tubType, parentWallIds}} fixture
 * @param {Array} walls room walls
 * @returns {'alcove'|'drop-in'|'undermount'|'freestanding'|null} null for
 *   non-tub fixtures
 */
function resolveTubType(fixture, walls) {
  const type = fixture?.type;
  if (type !== 'bathtub' && type !== 'tub-shower') return null;
  if (fixture.tubType) return fixture.tubType;
  if (type === 'tub-shower') return 'alcove';

  const hugged = countWallHuggedSides(fixture, walls);
  if (hugged >= ALCOVE_MIN_HUGGED_SIDES) return 'alcove';

  const w = fixture.dimensions?.x || 0;
  const d = fixture.dimensions?.z || 0;
  if (w >= FREESTANDING_MIN_WIDTH_M && d >= FREESTANDING_MIN_DEPTH_M) {
    return 'freestanding';
  }
  if (w >= LARGE_TUB_THRESHOLD_M && d >= LARGE_TUB_THRESHOLD_M) {
    return 'drop-in';
  }

  const seated = (fixture.parentWallIds?.length || 0) > 0 || hugged > 0;
  if (!seated) return 'freestanding';
  if (w >= DECK_MIN_WIDTH_M && d >= DECK_MIN_DEPTH_M) return 'undermount';
  return 'alcove';
}

module.exports = {
  boxSidesXZ,
  wallAxesWithFallback,
  crossingWallDistance,
  crossingObstacleDistance,
  widthAxisReach,
  availableWidthRun,
  alcoveRecess,
  wallToWallSpans,
  wallHuggedSideFlags,
  countWallHuggedSides,
  inferScannedTubType,
  resolveTubType,
  WALL_HUG_TOLERANCE_M,
  LARGE_TUB_THRESHOLD_M,
  DECK_MIN_WIDTH_M,
  DECK_MIN_DEPTH_M,
  FREESTANDING_MIN_WIDTH_M,
  FREESTANDING_MIN_DEPTH_M,
};
